import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AppContext } from '../contexts/AppContext';
import { getTranslation } from '../common/Translation';
import useFetchMedia from '../hooks/useFetchMedia';
import PlayIconSvg from "../assets/icon_play.svg?react";
import StopIconSvg from "../assets/icon_stop.svg?react";
import { MEDIA_PREFIX } from '../Settings';

interface MediaPopupProps {
  item: any,
  close?: () => void,
  caption?: string
}

const Container = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: initial !important;
  display: flex;
  align-items: center
`

const AudioItem = styled.div`
  margin: 0;
  float: left;
  padding: 40px;
  border-radius: 8px;
  width: 100%;
  display: flex;
  align-items: center;
  background-color: #333;
  margin: 0px 200px;
  z-index: 10;

  &:not(:last-child) {
    margin-bottom: 10px;
  }

  &.block {
    width: 150px;
    min-width: 150px;
    height: 150px;
    justify-content: center;
    position: relative;

    & button::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 70px;
    }

    & span, & div {
      display: none;
    }
  }
`;

const Title = styled.span`
  --max-lines: 1;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  font-size: 1.2em;
`;


const Button = styled.button`
  width: 80px;
  min-width: 80px;
  height: 80px;
  border-radius: 40px;
  margin-right: 30px;
  background-color: ${p => p.theme.accentColor};
  border: none;
  float: left;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  pointer-events: all;

  &:active {
    opacity: .8;
  }
`;

const AudioElement = styled.audio`
  display: none;
`;

const PlayIcon = styled(PlayIconSvg)`
  margin-left: 3px;
  height: 30px;
  width: 30px;
`

const StopIcon = styled(StopIconSvg)`
  height: 28px;
  width: 28px;
`

const Control = styled.div`
  width: 100%;
`;

const ProgressBar = styled.div`
  width: 100%;
  background-color: #FFFFFF15;
  height: 6px;
  border-radius: 2px;
  margin-top: 15px;
  position: relative;

  &::before {
    width: 100%;
    content: "";
    position: absolute;
    padding: 30px 0;
    transform: translateY(-50%);
  }
`;

const Progress = styled.div`
  width: 0;
  height: 6px;
  border-radius: 2px;
  background-color: ${p => p.theme.accentColor};
  transition: width .1s;
  pointer-events: none;
`;

const Caption = styled.div`
  width: 100%;
  text-align: center;
  position: absolute;
  bottom: 0;
  padding: 50px;
  background: linear-gradient(0deg, black, transparent);
`

/**
 * Render an audio popup
 * @returns {JSX.Element} Component template
 */
const PopupAudio: FC<MediaPopupProps> = ({item, close, caption}) => {
  const { currentLanguage, globalVolume, resetTimer } = useContext(AppContext);
  const [playbackPosition, setPlaybackPosition] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const audioElement = useRef<HTMLAudioElement>(null);
  const [itemAudio] = useFetchMedia(item);

  /**
   * Stop all audio
   */
   const stopAllAudio = useCallback(() => {
    const audioElements = document.querySelectorAll("audio");
    audioElements.forEach(el => {
      el.pause();
      el.currentTime = 0;
    });
  }, []);

  /**
   * Toggle audio placback
   */
   const togglePlay = useCallback(() => {
    if (isPlaying) {
      if (audioElement.current) {
        audioElement.current.pause();
        audioElement.current.currentTime = 0;
      }
      setIsPlaying(false);
    } else {
      stopAllAudio();
      if (audioElement.current) {
        audioElement.current.play();
        audioElement.current.currentTime = 0;
      }
      setIsPlaying(true);
    }
  }, [isPlaying, stopAllAudio]);

  useEffect(() => {
    if (audioElement.current && globalVolume !== undefined) {
      audioElement.current.volume = globalVolume;
    }
  }, [globalVolume]);

  // Listen for audio player time change
  useEffect(() => {
    if (audioElement.current) {
      audioElement.current?.addEventListener("timeupdate", e => {
        setPlaybackPosition(Number((audioElement.current?.currentTime || 0) / (audioElement.current?.duration || 0)) * 100);
        resetTimer && resetTimer();
      })

      audioElement.current?.addEventListener("ended", e => {
        setIsPlaying(false);
        close && close();
        if (audioElement.current) {
          audioElement.current.currentTime = 0;
          resetTimer && resetTimer();
        }
      })

      audioElement.current?.addEventListener("pause", e => {
        setIsPlaying(false);
      })

      audioElement.current?.addEventListener("play", e => {
        setIsPlaying(true);
      })
    }
  }, [audioElement, resetTimer, close]);

  /**
   * Update playback position on click
   */
  const updatePosition = useCallback((e: any) => {
    if (isPlaying && audioElement.current) {
      const boundingRect = e.target.getBoundingClientRect();
      audioElement.current.currentTime = Number((((e?.clientX - boundingRect?.left)) / (e?.target?.offsetWidth)) * audioElement.current.duration);
    }
  }, [isPlaying]);

  return (
    <>
      <Container>
        <AudioItem className={`showInPreview`}>
          <AudioElement ref={audioElement} src={itemAudio?.url ? `${MEDIA_PREFIX}${itemAudio?.url}` : undefined} autoPlay/>
          <Button onClick={togglePlay}>{isPlaying ? (<StopIcon/>) : (<PlayIcon/>)}</Button>
          <Control>
            {itemAudio?.title && (<Title>{getTranslation(itemAudio?.title, currentLanguage)}</Title>)}
            <ProgressBar onClick={updatePosition}><Progress style={{width: `${playbackPosition}%`}}/></ProgressBar>
          </Control>
        </AudioItem>
        {(item?.description) && (<Caption>{getTranslation(item?.description, currentLanguage)}</Caption>)}
      </Container>
    </>
  );
}

export default PopupAudio;